import React, { useState, useRef, useEffect } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import './AppLayout.css';
import Sidebar from '../Sidebar/Sidebar';
import MainContent from '../MainContent/MainContent';
import UtilityPanel from '../UtilityPanel/UtilityPanel';
import config from '../../config';
import HighwayIcon from '../../importfiles/Autostrada.png';
import BridgeIcon from '../../importfiles/Ponte.png';
import TunnelIcon from '../../importfiles/Tunnel.png';
import DamsIcon from '../../importfiles/Diga.png';
import RailwayIcon from '../../importfiles/Ferrovia.png';
import IconaGoals from '../../importfiles/IconaGoals.png';
import IconaSensor from '../../importfiles/IconaSensor.png'
import IconaConnetivity from '../../importfiles/IconConntetivity.png'
import IconaData from '../../importfiles/IconData.png'
import IconProposal from '../../importfiles/IconProposal.png'
import iconNormal from '../../importfiles/Occhiochiuso.svg';
import iconActive from '../../importfiles/Occhio.svg';
import V from '../../importfiles/V.png';
import LogoProgetto from '../../importfiles/Chord A.png';
import DeleteIcon from '../../importfiles/Delete.png';
import LogoutIcon from '../../importfiles/Logout.png';
import { StepIcon } from '../Icons/StepIcons';
import { findIconDefinition, library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import packageJson from '../../../package.json';

const APP_VERSION = packageJson.version;

library.add(fas);

const FALLBACK_FA_ICON = 'circle-question';
const DEFAULT_STEPS = [//solo default online?
    { id: 1, key: 'goal', label: 'Goal', active: true, completed: false },
    { id: 2, key: 'sensors', label: 'Sensors', active: false, completed: false },
    { id: 3, key: 'connectivity', label: 'Connectivity', active: false, completed: false },
    { id: 4, key: 'data_alerts', label: 'Data and alerts', active: false, completed: false },
    { id: 5, key: 'proposal', label: 'Proposals', active: false, completed: false },
];
export const HIDDEN_MESSAGE_PREFIX = "[INIT_CHAT]";


const AppLayout = ({ name, signOut, auth, profileImage,
    showProfileModal,
    setShowProfileModal,
    handleImageChange,
    fileInputRef, selections }) => {

    const user = { name: name || "User", profileImage: profileImage };
    const messageRefs = useRef({});
    const [messages, setMessages] = useState([]);
    const [steps, setSteps] = useState(DEFAULT_STEPS);
    // Sequenza ordinata degli step effettivamente attraversati durante la chat.
    // Consente ripetizioni non consecutive (es. goal -> sensors -> ... -> proposal -> sensors).
    const [stepJourney, setStepJourney] = useState([]);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [currentChatId, setCurrentChatId] = useState(null);
    const [history, setHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [selectedInfrastructure, setSelectedInfrastructure] = useState(null);
    const [showPersonalData, setShowPersonalData] = useState(false);
    const [showLoginData, setShowLoginData] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [selectedIndustry, setSelectedIndustry] = useState(selections?.industry || "Not selected");
    const [selectedJob, setSelectedJob] = useState(selections?.job || "Not selected");
    const [isEditingPassword, setIsEditingPassword] = useState(false);
    const [oldPassword, setOldPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmNewPassword, setConfirmNewPassword] = useState("");
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [showOldPassword, setShowOldPassword] = useState(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const [showChangeJobIndustry, setShowChangeJobIndustry] = useState(false);

    const infrastructures = [
        { id: 'dams', label: 'Dams', icon: DamsIcon },
        { id: 'highway', label: 'Highway', icon: HighwayIcon },
        { id: 'tunnel', label: 'Tunnel', icon: TunnelIcon },
        { id: 'bridge', label: 'Bridge', icon: BridgeIcon },
        { id: 'railway', label: 'Railway', icon: RailwayIcon },
        { id: 'proposal', label: 'Proposal', icon: IconProposal}
    ];

    useEffect(() => {
        if (selections?.industry) setSelectedIndustry(selections.industry);
        if (selections?.job) setSelectedJob(selections.job);
    }, [selections]);

    useEffect(() => {
        const loadAvailableSteps = async () => {
            try {
                const token = await getToken();
                const response = await fetch(`${config.apiBaseUrl}/api/steps/`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const stepsConfig = await response.json();
                const dynamicSteps = [...stepsConfig.data.steps]
                    .sort((a, b) => a.order - b.order)
                    .map((s, idx) => ({
                        id: idx + 1,
                        key: s.key,
                        label: s.label,
                        active: idx === 0,
                        completed: false,
                    }));
                setSteps(dynamicSteps);
            } catch (e) {
                console.error("Errore caricamento step, uso fallback statico:", e);
                setSteps(DEFAULT_STEPS);
            }
        };
        loadAvailableSteps();
    }, []);

    const getToken = async () => {
        // Always return the local backend JWT (HS256).
        // Cognito id_tokens (RS256) must NEVER be sent directly to the backend API.
        const localToken = localStorage.getItem('token');
        if (localToken) return localToken;

        // No local token yet (race condition after federated login) — exchange now.
        const exchangeCognito = async (idToken) => {
            const res = await fetch(`${config.apiBaseUrl}/api/auth/oidc`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ provider: 'cognito', token: idToken }),
            });
            if (!res.ok) return null;
            const data = await res.json();
            localStorage.setItem('token', data.data.access_token);
            if (data.data.refresh_token) localStorage.setItem('refresh_token', data.data.refresh_token);
            return data.data.access_token;
        };

        // Try OIDC id_token (react-oidc-context / federated login)
        if (auth?.user?.id_token) {
            const t = await exchangeCognito(auth.user.id_token).catch(() => null);
            if (t) return t;
        }

        // Fallback: Amplify session (email/password login via Cognito)
        try {
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();
            if (idToken) {
                const t = await exchangeCognito(idToken).catch(() => null);
                if (t) return t;
            }
        } catch (e) { /* no Amplify session */ }

        return null;
    };
    const ChangeJobIndustry = ({ isOpen, onClose }) => {
        if (!isOpen) return null;

        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000
            }}>
                <div style={{
                    backgroundColor: '#F5E6D3',
                    borderRadius: '16px',
                    border: '2px solid #C89B7B',
                    padding: '32px',
                    maxWidth: '400px',
                    width: '90%',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
                }}>
                    <h2 style={{
                        fontSize: '20px',
                        fontWeight: '400',
                        color: '#333',
                        marginBottom: '8px',
                        textAlign: 'center'
                    }}>
                        Got it!
                    </h2>
                    <p style={{
                        fontSize: '18px',
                        fontWeight: '400',
                        color: '#333',
                        marginBottom: '32px',
                        textAlign: 'center'
                    }}>
                        Are you sure  you want to<br />make this change?
                    </p>

                    <div style={{
                        display: 'flex',
                        gap: '12px',
                        justifyContent: 'center'
                    }}>
                        <button
                            onClick={onClose}
                            style={{
                                padding: '12px 32px',
                                backgroundColor: '#E5D4C1',
                                border: '1px solid #C89B7B',
                                borderRadius: '8px',
                                color: '#333',
                                fontSize: '16px',
                                cursor: 'pointer',
                                fontWeight: '400'
                            }}
                        >
                            Back
                        </button>
                        <button
                            onClick={() => { handleUpdateProfile(); setShowChangeJobIndustry(false); }}
                            style={{
                                padding: '12px 32px',
                                backgroundColor: 'transparent',
                                border: '1px solid #D84040',
                                borderRadius: '8px',

                                color: '#D84040',
                                fontSize: '16px',
                                cursor: 'pointer',
                                fontWeight: '400'
                            }}
                        >
                            Confirm
                        </button>
                    </div>
                </div>
            </div>
        );
    };
    const DeleteAccountModal = ({ isOpen, onClose, onConfirm }) => {
        if (!isOpen) return null;

        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000
            }}>
                <div style={{
                    backgroundColor: '#F5E6D3',
                    borderRadius: '16px',
                    border: '2px solid #C89B7B',
                    padding: '32px',
                    maxWidth: '400px',
                    width: '90%',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
                }}>
                    <h2 style={{
                        fontSize: '20px',
                        fontWeight: '400',
                        color: '#333',
                        marginBottom: '8px',
                        textAlign: 'center'
                    }}>
                        Sorry to see you go.
                    </h2>
                    <p style={{
                        fontSize: '18px',
                        fontWeight: '400',
                        color: '#333',
                        marginBottom: '32px',
                        textAlign: 'center'
                    }}>
                        Are you sure  you want to<br />Delete the account?
                    </p>

                    <div style={{
                        display: 'flex',
                        gap: '12px',
                        justifyContent: 'center'
                    }}>
                        <button
                            onClick={onClose}
                            style={{
                                padding: '12px 32px',
                                backgroundColor: '#E5D4C1',
                                border: '1px solid #C89B7B',
                                borderRadius: '8px',
                                color: '#333',
                                fontSize: '16px',
                                cursor: 'pointer',
                                fontWeight: '400'
                            }}
                        >
                            Back
                        </button>
                        <button
                            onClick={onConfirm}
                            style={{
                                padding: '12px 32px',
                                backgroundColor: 'transparent',
                                border: '1px solid #D84040',
                                borderRadius: '8px',
                                color: '#D84040',
                                fontSize: '16px',
                                cursor: 'pointer',
                                fontWeight: '400'
                            }}
                        >
                            Delete
                        </button>
                    </div>
                </div>
            </div>
        );
    };
    const LogOutAccountModal = ({ isOpen, onClose, onConfirm }) => {
        if (!isOpen) return null;

        return (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 1000
            }}>
                <div style={{
                    backgroundColor: '#F5E6D3',
                    borderRadius: '16px',
                    border: '2px solid #C89B7B',
                    padding: '32px',
                    maxWidth: '400px',
                    width: '90%',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
                }}>
                    <h2 style={{
                        fontSize: '20px',
                        fontWeight: '400',
                        color: '#333',
                        marginBottom: '8px',
                        textAlign: 'center'
                    }}>
                        Sorry to see you go.
                    </h2>
                    <p style={{
                        fontSize: '18px',
                        fontWeight: '400',
                        color: '#333',
                        marginBottom: '32px',
                        textAlign: 'center'
                    }}>
                        Are you sure you want to<br />Log the account?
                    </p>

                    <div style={{
                        display: 'flex',
                        gap: '12px',
                        justifyContent: 'center'
                    }}>
                        <button
                            onClick={onClose}
                            style={{
                                padding: '12px 32px',
                                backgroundColor: '#E5D4C1',
                                border: '1px solid #C89B7B',
                                borderRadius: '8px',
                                color: '#333',
                                fontSize: '16px',
                                cursor: 'pointer',
                                fontWeight: '400'
                            }}
                        >
                            Back
                        </button>
                        <button
                            onClick={onConfirm}
                            style={{
                                padding: '12px 32px',
                                backgroundColor: 'transparent',
                                border: '1px solid #D84040',
                                borderRadius: '8px',
                                color: '#D84040',
                                fontSize: '16px',
                                cursor: 'pointer',
                                fontWeight: '400'
                            }}
                        >
                            Logout
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // const getStepId = (key) => {
    //     const step = steps.find(s => s.key === key);
    //     return step ? step.id : 0;
    // };

    // const updateStepsUI = (savedSteps) => {
    //     if (!savedSteps || savedSteps.length === 0) {
    //         setSteps(DEFAULT_STEPS.map(s => ({ ...s })));
    //         return;
    //     }

    // const ensureChatContext = async (forceNew = false, specificId = null) => {
    //     const currentUserId = name || "default_user";
    //     const token = await getToken();

    //     // Decidiamo quale ID mandare:
    //     // Se forceNew è true, mandiamo vuoto per farne creare uno nuovo dal server.
    //     // Se c'è un specificId (es. caricamento history), usiamo quello.
    //     // Altrimenti usiamo quello corrente (se esiste).
    //     let targetId = specificId;
    //     if (!forceNew && !targetId) {
    //         targetId = currentChatId;
    //     }

    // TODO: Aggiornare questa API, la nuova è `${config.apiBaseUrl}/chat` senza chat_id per inizializzare una nuova chat, 
    // con chat_id per recuperare una chat già esistente
    //     let url = `${config.apiBaseUrl}/${currentUserId}/init-context`;
    //     if (targetId) {
    //         url += `?context_id=${targetId}`; // Passiamo l'ID al backend
    //     }

    //     const response = await fetch(url, {
    //         method: 'GET',
    //         headers: { 'Authorization': `Bearer ${token}` }
    //     });

    //     if (!response.ok) {
    //         throw new Error(`Errore inizializzazione contesto server: ${response.status}`);
    //     }

    //     const data = await response.json();

    //     // Aggiorniamo lo stato in modo centralizzato
    //     setCurrentChatId(data.context_id);

    //     return data.context_id;
    // };
    // --- HELPER PER AGGIORNARE LO STEP ATTUALE DURANTE LO STREAM ---
    const updateCurrentStep = (stepKey) => {
        if (!stepKey) return;

        const normalizedKey = stepKey.trim().toLowerCase();
        const targetStep = steps.find(s => s.key.toLowerCase() === normalizedKey);
        if (!targetStep) return;
        const canonicalKey = targetStep.key;

        // Aggiunge lo step al journey mantenendo l'ordine e le ripetizioni non consecutive.
        setStepJourney(prevJourney => {
            const lastKey = prevJourney[prevJourney.length - 1];
            // Evita duplicati consecutivi generati dai chunk successivi dello stesso step.
            if (lastKey && lastKey.toLowerCase() === canonicalKey.toLowerCase()) {
                return prevJourney;
            }
            return [...prevJourney, canonicalKey];
        });

        setSteps(prevSteps => prevSteps.map(s => ({
            ...s,
            active: s.key.toLowerCase() === normalizedKey,
            completed: s.id < targetStep.id
        })));
    };

    // --- HELPER PER RIPRISTINARE GLI STEP DALLA CRONOLOGIA ---
    const updateStepsUI = (savedSteps) => {
        if (!savedSteps || savedSteps.length === 0) {
            setStepJourney([]);
            setSteps(prev => prev.map(s => ({ ...s, active: false, completed: false })));
            return;
        }

        // Ricostruisce il journey collassando le ripetizioni CONSECUTIVE (una sola voce
        // per "visita" dello step), ma mantenendo le ripetizioni non adiacenti:
        // es. goal -> sensors -> connectivity -> sensors resta [goal, sensors, connectivity, sensors].
        const journeyKeys = savedSteps.reduce((acc, saved) => {
            const meta = steps.find(s =>
                s.key.toLowerCase() === saved.toLowerCase() ||
                s.label.toLowerCase() === saved.toLowerCase()
            );
            const canonicalKey = meta ? meta.key : saved;
            const lastKey = acc[acc.length - 1];
            if (!lastKey || lastKey.toLowerCase() !== canonicalKey.toLowerCase()) {
                acc.push(canonicalKey);
            }
            return acc;
        }, []);
        setStepJourney(journeyKeys);

        const lastSavedStep = savedSteps[savedSteps.length - 1];

        setSteps(prev => {
            const targetStep = prev.find(s =>
                s.key.toLowerCase() === lastSavedStep.toLowerCase() ||
                s.label.toLowerCase() === lastSavedStep.toLowerCase()
            );
            const targetId = targetStep ? targetStep.id : 0;

            return prev.map(s => ({
                ...s,
                active: s.key.toLowerCase() === lastSavedStep.toLowerCase() || s.label.toLowerCase() === lastSavedStep.toLowerCase(),
                completed: s.id < targetId
            }));
        });
    };

    // --- GESTORI EVENTI E INFRASTRUTTURA ---
    const handleSelectInfra = (id) => startCategoryChat(id);

    const handleUpdatePassword = async () => {
        if (!oldPassword || !newPassword || !confirmNewPassword) {
            alert("Please fill in the current password and both new password fields.");
            return;
        }
        if (newPassword !== confirmNewPassword) {
            alert("The new passwords do not match.");
            return;
        }
        setPasswordLoading(true);
        try {
            // Collect Cognito access token and id-token claims from whichever
            // session is active: react-oidc-context (hosted UI redirect) or
            // Amplify (email/password login).
            let cognitoAccessToken = auth?.user?.access_token || null;
            let idTokenClaims = auth?.user?.profile || null;

            if (!cognitoAccessToken || !idTokenClaims) {
                try {
                    const { fetchAuthSession } = await import('aws-amplify/auth');
                    const session = await fetchAuthSession();
                    cognitoAccessToken = cognitoAccessToken || session?.tokens?.accessToken?.toString() || null;
                    idTokenClaims = idTokenClaims || session?.tokens?.idToken?.payload || null;
                } catch (e) {
                    /* no Amplify session */
                }
            }

            if (cognitoAccessToken) {
                // Cognito user. Accounts federated from an external IdP (e.g. Google)
                // have no Cognito password and cannot change it here.
                const identities = idTokenClaims?.identities;
                const isExternalFederated = Array.isArray(identities)
                    ? identities.length > 0
                    : !!identities;
                if (isExternalFederated) {
                    alert("This account signs in through an external provider (e.g. Google) and has no password to change here.");
                    return;
                }

                // Change the Cognito password using the ChangePassword API
                // (authenticated via the access token, no AWS credentials needed).
                const response = await fetch(`https://cognito-idp.${config.cognito.region}.amazonaws.com/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/x-amz-json-1.1',
                        'X-Amz-Target': 'AWSCognitoIdentityProviderService.ChangePassword',
                    },
                    body: JSON.stringify({
                        AccessToken: cognitoAccessToken,
                        PreviousPassword: oldPassword,
                        ProposedPassword: newPassword,
                    }),
                });
                if (!response.ok) {
                    const err = await response.json().catch(() => ({}));
                    const reason = err.message || (err.__type || '').split('#').pop() || 'Password update failed';
                    throw new Error(reason);
                }
            } else {
                // Local backend account: call our endpoint.
                const token = await getToken();
                const response = await fetch(`${config.apiBaseUrl}/api/user/password`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
                });
                if (!response.ok) {
                    const errData = await response.json().catch(() => ({}));
                    throw new Error(errData.error || 'Password update failed');
                }
            }

            alert("Password updated successfully! ✅");
            setIsEditingPassword(false);
            setOldPassword("");
            setNewPassword("");
            setConfirmNewPassword("");
        } catch (error) {
            alert("Error: " + error.message);
        } finally {
            setPasswordLoading(false);
        }
    };

    const startCategoryChat = async (category) => {
        setSelectedInfrastructure(category);
        setStepJourney([]);
        try {
            const token = await getToken();
            const response = await fetch(`${config.apiBaseUrl}/api/chat/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    prompt: category,
                    kb: 'chord_a'
                })
            });
            // setIsTyping(false);
            const data = await response.json();
            const chatId = data?.data?.chat_id;
            if (!response.ok) {
                console.error("Creazione chat fallita", response.status, await response.text());
                return;
            }
            console.log("chat id nuovo", chatId);
            if (chatId) setCurrentChatId(chatId);

            // if (!response.body) return;

            // const reader = response.body.getReader();
            // const decoder = new TextDecoder();

            // let botMessage = { id: Date.now() + 1, text: "", isUser: false, files: [], stepReference: null };
            // setMessages(prev => [...prev, botMessage]);

            // while (true) {
            //     const { value, done } = await reader.read();
            //     if (done) break;

            //     const chunk = decoder.decode(value, { stream: true });

            //     let currentStepFound = null;
            //     const stepMatch = chunk.match(/\[STEP:(.*?)\]/);
            //     if (stepMatch) {
            //         currentStepFound = stepMatch[1].trim();
            //         updateCurrentStep(currentStepFound);
            //     }

            //     setMessages(prev => {
            //         const lastMsg = prev[prev.length - 1];
            //         if (lastMsg && !lastMsg.isUser && lastMsg.id === botMessage.id) {
            //             return [...prev.slice(0, -1), {
            //                 ...lastMsg,
            //                 text: lastMsg.text + chunk,
            //                 stepReference: currentStepFound || lastMsg.stepReference
            //             }];
            //         }
            //         return prev;
            //     });
            // }
        } catch (error) {
            console.error("Errore nell'avvio della chat:", error);
            // setIsTyping(false);
        }
    };

    const handleSendMessage = async (text) => {
        if (!text.trim()) return;
        setIsTyping(true);
        const newMessage = { id: Date.now(), text, isUser: true, files: [] };
        setMessages(prev => [...prev, newMessage]);

        try {
            const token = await getToken();
            const response = await fetch(`${config.apiBaseUrl}/api/chat/${currentChatId}/stream`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({
                    content: text,
                })
            });
            setIsTyping(false);

            if (!response.body) return;

            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let botMessage = { id: Date.now() + 1, text: "", isUser: false, files: [], stepReference: null };

            setMessages(prev => [...prev, botMessage]);

            // Il tag [STEP:...] può arrivare spezzato su più chunk durante lo stream,
            // quindi la regex va applicata sul testo accumulato, non sul singolo chunk.
            let accumulatedText = "";
            let currentStepFound = null;

            while (true) {
                const { value, done } = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value, { stream: true });
                accumulatedText += chunk;

                const stepMatch = accumulatedText.match(/\[STEP:(.*?)\]/);
                if (stepMatch) {
                    const stepFromChunk = stepMatch[1].trim();
                    if (stepFromChunk !== currentStepFound) {
                        currentStepFound = stepFromChunk;
                        updateCurrentStep(currentStepFound);
                    }
                }

                setMessages(prev => {
                    const lastMsg = prev[prev.length - 1];
                    if (lastMsg && !lastMsg.isUser && lastMsg.id === botMessage.id) {
                        return [...prev.slice(0, -1), {
                            ...lastMsg,
                            text: lastMsg.text + chunk,
                            stepReference: currentStepFound || lastMsg.stepReference
                        }];
                    }
                    return prev;
                });
            }
        } catch (error) {
            console.error(error);
            setIsTyping(false);
        }
    };

    const handleDownloadFile = async (fileId) => {
        if (!currentChatId || fileId == null) return;
        try {
            const token = await getToken();
            const response = await fetch(`${config.apiBaseUrl}/api/chat/${currentChatId}/files/${fileId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                console.error(`Download del file ${fileId} fallito: ${response.status}`);
                return;
            }

            // Ricava il nome del file dall'header Content-Disposition, con fallback.
            const disposition = response.headers.get('Content-Disposition') || '';
            const match = disposition.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
            const fileName = match
                ? decodeURIComponent(match[1].replace(/"/g, ''))
                : `documento-${fileId}.pdf`;

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error(error);
        }
    };

    const handleUpdateProfile = async () => {
        try {
            const token = await getToken();
            const response = await fetch(`${config.apiBaseUrl}/api/onboarding/complete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ industry: selectedIndustry, job: selectedJob })
            });
            if (response.ok) alert("Profile updated successfully!");
        } catch (err) {
            console.error(err);
        }
    };

    const handleLogout = async () => {
        try {
            const token = await getToken();
            if (token) {
                await fetch(`${config.apiBaseUrl}/api/auth/logout`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` },
                });
            }
        } catch (err) {
            console.error('Errore durante il logout:', err);
        } finally {
            localStorage.removeItem('token');
            localStorage.removeItem('refresh_token');
            if (signOut) signOut();
            else {
                localStorage.clear();
                window.location.reload();
            }
        }
    };

    const handleDeleteAccount = async () => {
        try {
            const token = await getToken();
            await fetch(`${config.apiBaseUrl}/api/auth/user`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` },
            });
        } catch (err) {
            console.error('Errore eliminazione account:', err);
        } finally {
            handleLogout();
        }
    };

    // Polling sullo stato del RAG: risolve quando l'indicizzazione è terminata
    // (status torna a IDLE) oppure rigetta in caso di errore/timeout.
    const pollRagStatus = async (chatId, { intervalMs = 2000, timeoutMs = 300000 } = {}) => {
        const token = await getToken();
        const headers = { 'Authorization': `Bearer ${token}` };
        if (user?.name) headers['X-User-ID'] = user.name;

        const startedAt = Date.now();

        while (Date.now() - startedAt < timeoutMs) {
            const response = await fetch(`${config.apiBaseUrl}/api/chat/${chatId}/status`, {
                method: 'GET',
                headers: headers
            });

            if (!response.ok) {
                throw new Error(`Status check fallito: ${response.status}`);
            }

            const payload = await response.json();
            const status = payload?.data?.status;

            if (status === 'ERROR') {
                throw new Error("Il server ha riportato un errore durante l'indicizzazione.");
            }

            // Indicizzazione terminata: qualunque stato diverso da INDEXING significa "pronto"
            if (status && status !== 'INDEXING') {
                return status;
            }

            await new Promise(resolve => setTimeout(resolve, intervalMs));
        }

        throw new Error("Timeout durante l'attesa dell'indicizzazione del documento.");
    };

    const handleFileChange = async (file) => {
        if (!file) return;
        const token = await getToken();
        const formData = new FormData();
        const headers = { 'Authorization': `Bearer ${token}` };

        if (user?.name) {
            headers['X-User-ID'] = user.name;
        }

        formData.append('file', file);
        try {
            const uploadResponse = await fetch(`${config.apiBaseUrl}/api/chat/${currentChatId}/upload`, {
                method: 'POST',
                body: formData,
                headers: headers
            });

            if (uploadResponse.ok) {
                // L'unica fonte di verità è il FileSchema restituito dal backend:
                // niente id/oggetti file inventati dal frontend.
                const uploadPayload = await uploadResponse.json();
                const uploadedFile = uploadPayload?.data;
                if (!uploadedFile) {
                    throw new Error("Risposta di upload priva dei dati del file.");
                }
                const backendId = uploadedFile.id;
                const displayName = uploadedFile.file_name;

                // Mostra subito il file in stato "Processing" mentre il RAG è in corso
                setUploadedFiles(prev => [...prev, { name: displayName, status: 'Processing', id: backendId }]);

                try {
                    // Avvia l'indicizzazione RAG asincrona (l'endpoint ritorna subito)
                    const ragResponse = await fetch(`${config.apiBaseUrl}/api/chat/${currentChatId}/rag`, {
                        method: 'POST',
                        headers: headers
                    });

                    if (!ragResponse.ok) {
                        throw new Error(`Avvio RAG fallito: ${ragResponse.status}`);
                    }

                    // Polling su /status finché l'indicizzazione non è terminata
                    await pollRagStatus(currentChatId);

                    // RAG completato: passa il file allo stato "Analyzed"
                    setUploadedFiles(prev => prev.map(f =>
                        f.id === backendId ? { ...f, status: 'Analyzed' } : f
                    ));
                } catch (ragError) {
                    console.error("Errore durante l'analisi RAG:", ragError);
                    setUploadedFiles(prev => prev.map(f =>
                        f.id === backendId ? { ...f, status: 'Error' } : f
                    ));
                }
            } else {
                const errorData = await uploadResponse.json();
                console.error(`Errore dal server durante l'upload:`, errorData);
                alert(`Errore: ${errorData.error || uploadResponse.status}`);
            }
        } catch (error) {
            console.error("Errore di rete:", error);
            alert(`Errore di connessione. Non è stato possibile raggiungere il server per caricare "${file.name}".`);
        }
    };

    const handleFetchHistory = async () => {
        try {
            const token = await getToken();
            const response = await fetch(`${config.apiBaseUrl}/api/chat/history`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setHistory(data.data.chats);
            setShowHistory(!showHistory);
        } catch (error) {
            console.error(error);
        }
    };

    const loadChat = async (chatId) => {
        try {

            const token = await getToken();
            const response = await fetch(`${config.apiBaseUrl}/api/chat/${chatId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const payload = await response.json();
            const data = payload?.data ?? payload;

            setCurrentChatId(chatId);

            const loadedMessages = data.messages || [];

            setMessages(loadedMessages.map((msg, index) => ({
                id: msg.timestamp ?? msg.created_on ?? index,
                text: msg.content,
                isUser: msg.role.toLowerCase() === 'user',
                stepReference: msg.step || null
            })));

            // Ricostruisce la sequenza degli step attraversati dai messaggi salvati.
            const savedSteps = loadedMessages
                .map(msg => msg.step)
                .filter(Boolean);
            updateStepsUI(savedSteps);

            setUploadedFiles(data.files.map(file => ({
                id: file.id,
                name: file.file_name,
                status: 'Analyzed'
            })));

            setShowHistory(false);

        } catch (error) {
            console.error(error);
        }
    };

    const closeAllModals = () => {
        setShowProfileModal(false);
        setShowPersonalData(false);
        setShowLoginData(false);
        setShowDeleteConfirm(false);
        setShowLogoutConfirm(false);
    };

    const handleStepClick = (stepKey) => {
        // Punta all'ULTIMO messaggio di quel tipo di step (non al primo),
        // così cliccando uno step si va all'occorrenza più recente.
        const targetMessage = [...messages]
            .reverse()
            .find(m => m.stepReference?.toLowerCase() === stepKey.toLowerCase());
        if (targetMessage && messageRefs.current[targetMessage.id]) {
            messageRefs.current[targetMessage.id].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    };

    // Lista di step mostrata nel pannello: riflette il journey reale (con ripetizioni).
    // Se il journey è vuoto (chat appena avviata) si ricade sul catalogo statico.
    const journeySteps = stepJourney.map((key, index) => {
        const meta = steps.find(s => s.key.toLowerCase() === key.toLowerCase());
        return {
            id: meta?.id ?? index + 1,
            key: meta?.key ?? key,
            label: meta?.label ?? key,
            active: index === stepJourney.length - 1,
            completed: index < stepJourney.length - 1,
        };
    });

    const displaySteps = stepJourney.length > 0 ? journeySteps : steps;

    return (

        <div className="app-container">
            <Sidebar
                user={{ ...user, profileImage }}
                onLogout={handleLogout}
                onNewQuote={() => {
                    setMessages([]);
                    setSelectedInfrastructure(null);
                    setCurrentChatId(null);
                    setUploadedFiles([]);
                    setSteps(DEFAULT_STEPS.map(s => ({ ...s, })));
                    setStepJourney([]);
                }}
                onFetchHistory={handleFetchHistory}
                history={history}
                showHistory={showHistory}
                onLoadChat={(id) => { loadChat(id); setSelectedInfrastructure('history'); }}
                onOpenProfile={() => setShowProfileModal(true)}
            />

            <main className="main-viewport">
                <div className="mobile-header">
                    <img src={LogoProgetto} alt="Chord A" className="mobile-header-logo" />
                </div>
                <div className="mobile-header-divider" />
                {!selectedInfrastructure ? (
                    <div className="selection-screen2" >
                        <div className="selection-content">
                            <h1 style={{ fontSize: '1.8rem', marginBottom: '20px' }}>Hello, what would you like to monitor with IoT and Cloud?</h1>
                            <div className="infra-grid2">
                                {infrastructures.map(infra => (
                                    <button key={infra.id} className="infra-card2" onClick={() => handleSelectInfra(infra.id)}>
                                        <img src={infra.icon} alt={infra.label} className="infra-custom-icon" />
                                        <span className="infra-label">{infra.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="right-panel-column2">
                            <UtilityPanel
                                steps={displaySteps}
                                uploadedFiles={uploadedFiles}
                                onStepClick={handleStepClick}
                            />
                        </div>

                    </div>


                ) : (
                    <div className="chat-interface-wrapper" >
                        <MainContent
                            messages={messages}
                            messageRefs={messageRefs}
                            onSendMessage={handleSendMessage}
                            onFileUpload={handleFileChange}
                            onDownloadFile={handleDownloadFile}
                            isTyping={isTyping}
                        />

                        <div className="right-panel-column">
                            <UtilityPanel
                                steps={displaySteps}
                                uploadedFiles={uploadedFiles}
                                onStepClick={handleStepClick}
                            />
                        </div>


                    </div>
                )}


            </main>

            {showProfileModal && !showPersonalData && !showLoginData && !showDeleteConfirm && (
                <div className="profile-modal-overlay" onClick={closeAllModals}>
                    <div className="profile-modal-card-new" onClick={(e) => e.stopPropagation()}>
                        <button className="profile-back-btn" onClick={closeAllModals}>←</button>
                        <button className="profile-delete-btn2" onClick={() => setShowLogoutConfirm(true)}><img src={LogoutIcon} alt="Logout" className="logout-icon" /> </button>

                        <div className="profile-avatar-section">
                            <div className="profile-avatar-wrapper">
                                <div className="user-profile" >
                                    <div className="user-avatar2">
                                        {user?.profileImage ? (
                                            <img src={user.profileImage} alt="Profile" className="sidebar-avatar-img" onClick={() => fileInputRef.current.click()}
                                                style={{ cursor: 'pointer' }} />
                                        ) : (
                                            <span className="avatar-placeholder" onClick={() => fileInputRef.current.click()}
                                                style={{ cursor: 'pointer' }}>👤</span>
                                        )}
                                    </div>
                                </div>


                                <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*" onChange={handleImageChange} />
                            </div>
                        </div>
                        <div className="profile-greeting">Hi, {name}</div>
                        <button className="profile-menu-btn" onClick={() => setShowPersonalData(true)}>Personal data</button>
                        <button className="profile-menu-btn2" onClick={() => setShowLoginData(true)}>Login data</button>
                        <h1 style={{
                            alignItems: 'center',
                            background: '#0000',
                            border: 'none',
                            color: '#5d4037',
                            cursor: 'pointer',
                            display: 'flex',
                            fontSize: '15px',
                            gap: '12px',
                            padding: '20px',
                            textAlign: 'left',
                            transition: 'transform .2s',
                            position: 'absolute',
                            top: '580px',
                        }}>
                            Release {APP_VERSION}
                        </h1>
                        <button className="profile-delete-btn3" onClick={() => setShowDeleteConfirm(true)}><img src={DeleteIcon} alt="Delete" className="delete-icon" /> </button>
                    </div>
                </div>
            )}

            {showPersonalData && (
                <div className="profile-modal-overlay" onClick={() => setShowPersonalData(false)}>
                    <div className="profile-modal-card-new" onClick={(e) => e.stopPropagation()}>
                        <button className="profile-back-btn" onClick={() => setShowPersonalData(false)}>←</button>
                        <h2 className="profile-section-title">Personal data</h2>

                        <div className="profile-data-section">
                            <h3 className="profile-subsection-title">Industry</h3>
                            <div className='contenitore'>
                                {["Environmental Engineer", "Civil Engineer", "Service Manager", "Account Manager", "Sales", "Industrial Designer"].map((item) => (
                                    <div key={item}
                                        className={`profile-option-item ${selectedIndustry === item ? "active" : ""}`}
                                        onClick={() => setSelectedIndustry(item)}>
                                        {item}
                                        &nbsp;
                                        &nbsp;
                                        &nbsp;
                                        &nbsp;
                                        <button className="cerchietto" >
                                            {selectedIndustry === item && (
                                                <img
                                                    src={V}
                                                    alt="Icona Attiva"
                                                    className='icona-spunto'
                                                />
                                            )}
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="profile-data-section">
                            <h3 className="profile-subsection-title">Job</h3>
                            <div className='contenitore'>
                                {["Environmental Engineer", "Energy", "Cosmetics", "Civil Engineer", "Industrial Designer,IOT/Sensors"].map((item) => (
                                    <div key={item}
                                        className={`profile-option-item ${selectedJob === item ? "active" : ""}`}
                                        onClick={() => setSelectedJob(item)}>
                                        {item}
                                        &nbsp;
                                        &nbsp;
                                        &nbsp;
                                        &nbsp;
                                        <button className="cerchietto" >
                                            {selectedJob === item && (
                                                <img
                                                    src={V}
                                                    alt="Icona Attiva"
                                                    className='icona-spunto'
                                                />
                                            )}
                                        </button>

                                    </div>
                                ))}
                            </div>
                            <div style={styles.row}>
                                <button style={styles.btnGhost} onClick={() => setShowPersonalData(false)}>Back</button>
                                <button
                                    style={styles.btnBeige}
                                    disabled={!selections.job}
                                    onClick={() => setShowChangeJobIndustry(true)}
                                >
                                    Continue
                                </button>
                            </div>


                        </div>
                    </div>
                </div>
            )}

            {showLoginData && (
                <div className="profile-modal-overlay" onClick={() => setShowLoginData(false)}>
                    <div className="profile-modal-card-new" onClick={(e) => e.stopPropagation()}>
                        <button className="profile-back-btn"
                            onClick={() => {
                                setShowLoginData(false);
                                setIsEditingPassword(false);
                            }
                            }>←</button>
                        <div className="profile-avatar-section">

                            <div className="profile-avatar-wrapper">
                                <div className="user-profile" >
                                    <div className="user-avatar2">
                                        {user?.profileImage ? (
                                            <img src={user.profileImage} alt="Profile" className="sidebar-avatar-img" />
                                        ) : (
                                            <span className="avatar-placeholder">👤</span>
                                        )}
                                    </div>
                                </div>


                                <input type="file" ref={fileInputRef} style={{ display: 'none' }} accept="image/*" onChange={handleImageChange} />
                            </div>
                        </div>
                        <div className="profile-greeting">Hi, {name}</div>
                        <div className="profile-password-section" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {isEditingPassword ? (
                                <>
                                    <input type="password" value={oldPassword} onChange={(e) => setOldPassword(e.target.value)} className="paswoord-style" placeholder="Current password" />
                                    <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="paswoord-style" placeholder="New password" />
                                    <input type="password" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)} className="paswoord-style" placeholder="Confirm new password" />
                                    <button className="infra-card" onClick={handleUpdatePassword} disabled={passwordLoading}>
                                        {passwordLoading ? "Updating..." : "Confirm New Password"}
                                    </button>

                                </>
                            ) : (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '16px 20px',
                                    backgroundColor: '#C89B7B',
                                    borderRadius: '12px',
                                    border: 'none'
                                }}>

                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <input
                                            style={{ color: 'white', fontSize: '16px' }}
                                            type={showOldPassword ? "text" : "password"}
                                            value={oldPassword}
                                            onChange={(e) => setOldPassword(e.target.value)}
                                            className="paswoord-style"
                                            placeholder="Current password"
                                        />
                                        <button
                                            onClick={() => setShowOldPassword(!showOldPassword)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                padding: '0',
                                                display: 'flex',
                                                alignItems: 'center'
                                            }}
                                            onMouseEnter={() => setIsHovered(true)}
                                            onMouseLeave={() => setIsHovered(false)}
                                        >
                                            <img src={isHovered ? iconActive : iconNormal} alt="icon" />
                                        </button>
                                        <button
                                            onClick={() => setIsEditingPassword(true)}
                                            style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                padding: '0',
                                                display: 'flex',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showDeleteConfirm && (
                <DeleteAccountModal
                    isOpen={showDeleteConfirm}
                    onClose={() => setShowDeleteConfirm(false)}

                    onConfirm={handleDeleteAccount}
                />
            )}

            {showLogoutConfirm && (
                <LogOutAccountModal
                    isOpen={showLogoutConfirm}
                    onClose={() => setShowLogoutConfirm(false)}
                    onConfirm={() => {
                        setShowLogoutConfirm(false);
                        handleLogout();
                    }}
                />
            )}
            {showChangeJobIndustry && (
                <ChangeJobIndustry
                    isOpen={showChangeJobIndustry}
                    onClose={() => setShowChangeJobIndustry(false)}
                />
            )}
        </div>
    );
};

const styles =
{
    row: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '20px',
        marginTop: '24px'
    },
    btnGhost: {
        background: 'none',
        color: '#BC8F6F',
        cursor: 'pointer',
        fontSize: '1rem',
        border: ' 1px solid rgb(188, 143, 111)',
        borderRadius: '20px',
        padding: '12px 32px'
    },
    btnBeige: {
        padding: '12px 32px',
        borderRadius: '50px',
        border: '1px solid #C4B599',
        backgroundColor: '#E8DCC4',
        cursor: 'pointer',
        fontSize: '1rem',
    },
}

export default AppLayout;